/**
 * Command K — assembling the flat render list, including where the Ask row goes.
 *
 * Pure. This is the file that encodes the single most-argued decision in the
 * design, so it is worth stating the rule before the code:
 *
 *   **Never put a conversation in front of an action that already works in
 *   one click.**
 *
 * A GUI failure is bounded and self-correcting — you clicked the wrong thing
 * and the alternatives are still on screen. A conversational failure is
 * unbounded: the system did not understand and you must now guess a different
 * phrasing with no cue about what would work. That is why a bad answer
 * generalises to "this does not understand me", and why the results list is
 * the safety net. Every rule below follows from that one line.
 */

import { CMDK_GROUP_ORDER, cmdkRowDomId, kindGroup } from './cmdk.model';
import type { CmdkAskItem, CmdkGroup, CmdkItem, CmdkRenderRow } from './cmdk.model';
import type { CmdkScored } from './cmdk-ranking';

/** One group's contribution, before flattening. */
export interface CmdkGroupBlock {
  readonly group: CmdkGroup;
  readonly label: string;
  readonly items: readonly CmdkItem[];
  /** Render a skeleton instead of rows — a remote provider is in flight. */
  readonly loading?: boolean;
  /** Render an error banner instead of rows — this provider failed. */
  readonly failed?: boolean;
}

export interface CmdkAskPlacement {
  readonly item: CmdkAskItem;
  /** Header shown above the ask row when it lands at the end of a filter. */
  readonly notInFilterLabel: string;
}

export interface CmdkBuildOptions {
  /** Present only when AI is on and the query is non-empty. */
  readonly ask?: CmdkAskPlacement | null;
  /** A scope chip other than `all` is active. */
  readonly scoped: boolean;
  /** The trimmed query, for the strong-match test. */
  readonly query: string;
  /** The group order, when the surface has its own (`CMDK_MAP_GROUP_ORDER`); `CMDK_GROUP_ORDER` otherwise. */
  readonly groupOrder?: readonly CmdkGroup[];
}

/**
 * Does the top result already answer the query in one click?
 *
 * The test is a case-insensitive TITLE PREFIX, not a fuzzy score. A prefix is
 * something the user can see they typed the beginning of; a high fuzzy score
 * is a judgement they cannot inspect, and demoting the ask on a judgement they
 * cannot inspect is how "why is it not offering to help?" starts.
 */
export function hasStrongMatch(top: CmdkItem | undefined, query: string): boolean {
  const q = query.trim().toLowerCase();
  if (!top || q.length === 0) return false;
  return top.title.toLowerCase().startsWith(q);
}

/**
 * Flatten grouped blocks into the render list, placing the Ask row.
 *
 * Placement, in the order the conditions are checked:
 *
 *  - **A scope chip is active** → the ask goes to the very END, under a
 *    "Not in this filter?" header. The user narrowed deliberately; the ask
 *    does not jump the queue, it is the way back out of the filter.
 *  - **The top result is an exact prefix of the query** → that result keeps
 *    slot one and the ask drops to slot two. The retreat rule.
 *  - **Otherwise** → the ask is the first row.
 *  - **No rows at all** → the ask is the only row, and the caller auto-runs
 *    it. Asking permission at the moment confidence is lowest is the worst
 *    possible time to ask.
 */
export function buildRows(
  blocks: readonly CmdkGroupBlock[],
  options: CmdkBuildOptions,
): CmdkRenderRow[] {
  const order = options.groupOrder ?? CMDK_GROUP_ORDER;
  const ordered = [...blocks].sort((a, b) => order.indexOf(a.group) - order.indexOf(b.group));

  const rows: CmdkRenderRow[] = [];
  let flatIndex = 0;

  const pushItem = (item: CmdkItem): void => {
    rows.push({ type: 'row', item, flatIndex, domId: cmdkRowDomId(flatIndex) });
    flatIndex++;
  };

  const ask = options.ask ?? null;
  const firstItem = ordered.find(b => !b.loading && !b.failed && b.items.length > 0)?.items[0];
  const askGoesFirst = ask !== null && !options.scoped && !hasStrongMatch(firstItem, options.query);
  const askGoesSecond = ask !== null && !options.scoped && !askGoesFirst;

  if (askGoesFirst) pushItem(ask.item);

  let placedSecond = false;

  for (const block of ordered) {
    if (block.failed) {
      // Rows a failed source still has (Apps keeps Workplace's own apps when
      // the organisation's links fail) come first; the banner follows them.
      rows.push({ type: 'header', group: block.group, label: block.label });
      for (const item of block.items) pushItem(item);
      rows.push({ type: 'error', group: block.group });
      continue;
    }
    if (block.loading) {
      rows.push({ type: 'header', group: block.group, label: block.label });
      rows.push({ type: 'skeleton', group: block.group, count: 3 });
      continue;
    }
    if (block.items.length === 0) continue;

    rows.push({ type: 'header', group: block.group, label: block.label });
    for (const item of block.items) {
      pushItem(item);
      // Slot two, immediately after the one-click match that earned slot one.
      if (askGoesSecond && !placedSecond && ask) {
        pushItem(ask.item);
        placedSecond = true;
      }
    }
  }

  // A scoped ask, or an ask with no results to sit beside, lands at the end.
  if (ask && !askGoesFirst && !placedSecond) {
    if (options.scoped) {
      rows.push({ type: 'header', group: 'not-in-filter', label: ask.notInFilterLabel });
    }
    pushItem(ask.item);
  }

  // Nothing matched, and there was no ask to fall back on — AI off for this
  // tenant, or a scope that came back empty. Without this the column renders
  // literally nothing: a blank half-panel with the count reading "0 results"
  // somewhere else entirely, which reads as the palette having broken rather
  // than as the query having missed.
  //
  // `flatIndex` is never assigned here, so the state is not selectable and the
  // arrow keys have nothing to land on — which is correct: there is nothing to
  // open. The group is 'not-in-filter' because that is the one group label
  // that already means "this is about the query, not about a data source".
  if (flatIndex === 0 && !rows.some(r => r.type === 'skeleton' || r.type === 'error')) {
    rows.push({ type: 'empty', group: 'not-in-filter' });
  }

  return rows;
}

/** Ranked scored items back to plain items, for `CmdkGroupBlock.items`. */
export function toItems(scored: readonly CmdkScored[], limit?: number): CmdkItem[] {
  const items = scored.map(s => s.item);
  return typeof limit === 'number' ? items.slice(0, limit) : items;
}

/** Group ranked items by the group their kind belongs to, preserving order. */
export function groupByKind(scored: readonly CmdkScored[]): Map<CmdkGroup, CmdkItem[]> {
  const out = new Map<CmdkGroup, CmdkItem[]>();
  for (const entry of scored) {
    // An item may name its own block — see `CmdkItemBase.group`.
    const group = entry.item.group ?? kindGroup(entry.item.kind);
    const bucket = out.get(group);
    if (bucket) bucket.push(entry.item);
    else out.set(group, [entry.item]);
  }
  return out;
}
